/**
 * D-EZ-110 — THE WINTER, AS CHAPTERS: Silly Season, the rookie class, Hall of
 * Fame night and the preseason preview, between one season and the next.
 *
 * Shown on Home under the next-season card and at the top of Career → The
 * winter. Each chapter is folded only when its tab is opened (a winter is a
 * few hundred milliseconds of reading), and every one of them reads the league
 * through the winter after `seasonNumber` — never a race of the season ahead.
 *
 * WRITTEN, NEVER GENERATED: no model is called for any of this (the Sept 12
 * ruling covers the preview edition too), so it may appear on its own.
 */

import { useMemo, useState } from 'react';
import {
  hallOfFameNight,
  preseasonPreview,
  rookieClass,
  sillySeason,
  type ChapterSeason,
} from '../../domain/chapters/index.ts';
import { HallOfFameNightView, PreseasonPreviewView, RookieClassView, SillySeasonView } from './ChapterViews.tsx';

type Tab = 'winter' | 'rookies' | 'hof' | 'preview';

export interface WinterChaptersProps {
  /** Every season of the league the shell can reach, oldest first. */
  seasons: readonly ChapterSeason[];
  /** The settled season whose winter this is. */
  seasonNumber: number;
  onDriver?: (driverId: string) => void;
  /** Which chapter opens first. */
  initial?: Tab;
}

export default function WinterChapters({ seasons, seasonNumber, onDriver, initial = 'winter' }: WinterChaptersProps) {
  const [tab, setTab] = useState<Tab>(initial);
  /*
    The shell hands a fresh array on every render (Home spreads its history);
    the chapters are keyed on WHICH seasons are in it, so a re-render does not
    re-read the league.
  */
  const key = seasons.map((s) => `${s.season.seasonId}#${s.seasonNumber}${s.offseason ? 'w' : ''}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const through = useMemo(() => seasons.filter((s) => s.seasonNumber <= seasonNumber), [key, seasonNumber]);
  const winter = useMemo(() => (tab === 'winter' ? safe(() => sillySeason(through, seasonNumber)) : null), [tab, through, seasonNumber]);
  const rookies = useMemo(() => (tab === 'rookies' ? safe(() => rookieClass(through, seasonNumber)) : null), [tab, through, seasonNumber]);
  const night = useMemo(() => safe(() => hallOfFameNight(through, seasonNumber + 1)), [through, seasonNumber]);
  const preview = useMemo(() => (tab === 'preview' ? safe(() => preseasonPreview(through, seasonNumber + 1)) : null), [tab, through, seasonNumber]);
  const settled = through.some((s) => s.seasonNumber === seasonNumber && s.offseason);
  if (!settled) return null;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'winter', label: 'Silly Season' },
    { id: 'rookies', label: 'The rookie class' },
    ...(night ? [{ id: 'hof' as Tab, label: 'Hall of Fame night' }] : []),
    { id: 'preview', label: `Season ${seasonNumber + 1} preview` },
  ];

  return (
    <section className="chapbook readable" aria-label={`The winter after season ${seasonNumber}`}>
      <nav className="chapbook__tabs" aria-label="Winter chapters">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`seg${tab === t.id ? ' is-on' : ''}`}
            aria-current={tab === t.id ? 'true' : undefined}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>
      {tab === 'winter' && (winter ? <SillySeasonView chapter={winter} onDriver={onDriver} /> : <Empty what="the winter" />)}
      {tab === 'rookies' && (rookies ? <RookieClassView rookies={rookies} onDriver={onDriver} /> : <p className="chap__note">No rookies were licensed this winter.</p>)}
      {tab === 'hof' && night && <HallOfFameNightView night={night} onDriver={onDriver} />}
      {tab === 'preview' && (preview ? <PreseasonPreviewView preview={preview} onDriver={onDriver} /> : <Empty what="the preview" />)}
    </section>
  );
}

function Empty({ what }: { what: string }) {
  return <p className="chap__note">There is not enough of this league on the shelf to write {what}.</p>;
}

/** A chapter that cannot be read off a thin or old save is left out, never a crash. */
function safe<T>(fn: () => T): T | null {
  try {
    return fn();
  } catch {
    return null;
  }
}
